import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { formatVolumeGB } from '@/utils/function'
import React, { useMemo, useState } from 'react'
import { Typogaphy } from 'vnetwork-ui'
import { Viewers } from '../../types'
import countries from '../LocationViewer/countries.json'

type Row = Viewers[number]

interface LocationTableProps {
    viewers: Viewers
}

// Map ISO country code (lowercase in data) to a display label; fall back to the
// raw code when it isn't in the lookup so unknown geos still render.
const countryLabel = (code: string) =>
    countries.find((c) => c.value === code?.toLowerCase())?.label ||
    code?.toUpperCase() ||
    '-'

const percent = (value: number) => `${(value || 0).toFixed(2)}%`

const columns: DataTableColumn<Row>[] = [
    {
        key: 'country',
        title: 'Country',
        render: (_v, r) => countryLabel(r.country),
    },
    {
        key: 'volume',
        title: 'Volume',
        render: (_v, r) => formatVolumeGB(r.volume),
    },
    {
        key: 'volumePercentage',
        title: 'Volume %',
        width: 120,
        render: (_v, r) => percent(r.volumePercentage),
    },
    {
        key: 'requests',
        title: 'Requests',
        render: (_v, r) => (r.requests || 0).toLocaleString('en-US'),
    },
    {
        key: 'requestPercentage',
        title: 'Requests %',
        width: 120,
        render: (_v, r) => percent(r.requestPercentage),
    },
]

const LocationTable: React.FC<LocationTableProps> = ({ viewers = [] }) => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const rows = useMemo(
        () =>
            [...(viewers || [])].sort(
                (a, b) => (b?.volume || 0) - (a?.volume || 0)
            ),
        [viewers]
    )
    const paged = rows.slice((page - 1) * pageSize, page * pageSize)

    return (
        <div className="bg-white rounded-[8px] p-4">
            <div className="mb-2">
                <Typogaphy size="medium" className="text-primary font-semibold">
                    Traffic by Location
                </Typogaphy>
            </div>
            <DataTableCustomized<Row>
                columns={columns}
                rows={paged}
                rowKey={(row) => row.country}
                size="small"
                bordered
                emptyMessage="No data"
                pagination={{
                    page,
                    pageSize,
                    total: rows.length,
                    pageSizeOptions: [10, 20, 50],
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                }}
            />
        </div>
    )
}

export default LocationTable
